import React, { useState } from 'react';
import { JobForm } from '../../components/HR/JobForm';
import CandidateRatingForm from '../../components/HR/CandidateRatingForm';
import { PageHeader } from '../../components/Common';
import type { Job, Application } from './types';
import { useHiringData } from './useHiringData';
import HiringStats from './HiringStats';
import JobsTab from './JobsTab';
import ApplicationsTab from './ApplicationsTab';
import JobDetailsModal from './JobDetailsModal';
import ApplicationDetailsModal from './ApplicationDetailsModal';
import CandidateFormModal from './CandidateFormModal';

export const Hiring: React.FC = () => {
  const {
    jobs, applications, assessmentPoints,
    deleteJob, saveJob, saveRating, submitCandidate,
  } = useHiringData();

  const [activeTab, setActiveTab] = useState<'jobs' | 'applications'>('jobs');
  const [searchTerm, setSearchTerm] = useState('');
  const [showJobForm, setShowJobForm] = useState(false);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [showRatingForm, setShowRatingForm] = useState(false);
  const [ratingApplication, setRatingApplication] = useState<Application | null>(null);
  const [showCandidateForm, setShowCandidateForm] = useState(false);
  const [editingApplication, setEditingApplication] = useState<Application | null>(null);
  const [selectedJobForApply, setSelectedJobForApply] = useState<Job | null>(null);
  const [viewingJob, setViewingJob] = useState<Job | null>(null);
  const [viewingApplication, setViewingApplication] = useState<Application | null>(null);

  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [applicationStatusFilter, setApplicationStatusFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');

  const handleAddJob = () => { setEditingJob(null); setShowJobForm(true); };
  const handleEditJob = (job: Job) => { setEditingJob(job); setShowJobForm(true); };

  const handleSaveJob = async (jobData: Omit<Job, 'id' | 'applicants'>) => {
    await saveJob(jobData, editingJob);
    setShowJobForm(false);
    setEditingJob(null);
  };

  const handleRatingSave = async (ratings: any[]) => {
    if (!ratingApplication) return;
    await saveRating(ratings, ratingApplication.id);
    setShowRatingForm(false);
    setRatingApplication(null);
  };

  const handleApplyToJob = (job: Job) => {
    setSelectedJobForApply(job);
    setEditingApplication(null);
    setShowCandidateForm(true);
  };

  const handleEditApplication = (application: Application) => {
    setEditingApplication(application);
    setSelectedJobForApply(null);
    setShowCandidateForm(true);
  };

  const handleCandidateSubmit = async (data: any) => {
    await submitCandidate(data, editingApplication, selectedJobForApply);
    setShowCandidateForm(false);
    setEditingApplication(null);
    setSelectedJobForApply(null);
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setDepartmentFilter('all');
    setLocationFilter('all');
    setTypeFilter('all');
    setStatusFilter('all');
    setApplicationStatusFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
  };

  const headerActions = activeTab === 'jobs' ? [{
    label: 'Post New Job',
    onClick: handleAddJob,
    variant: 'info' as const,
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
  }] : undefined;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Hiring Management"
        description="Manage job postings and applications"
        actions={headerActions}
      />

      <HiringStats jobs={jobs} applications={applications} />

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('jobs')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'jobs'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Job Postings
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'applications'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            Applications
          </button>
        </nav>
      </div>

      {activeTab === 'jobs' ? (
        <JobsTab
          jobs={jobs}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          departmentFilter={departmentFilter}
          onDepartmentFilterChange={setDepartmentFilter}
          locationFilter={locationFilter}
          onLocationFilterChange={setLocationFilter}
          typeFilter={typeFilter}
          onTypeFilterChange={setTypeFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          dateFromFilter={dateFromFilter}
          onDateFromFilterChange={setDateFromFilter}
          dateToFilter={dateToFilter}
          onDateToFilterChange={setDateToFilter}
          onClearFilters={clearAllFilters}
          onAddJob={handleAddJob}
          onEditJob={handleEditJob}
          onDeleteJob={deleteJob}
          onViewJob={setViewingJob}
          onApplyToJob={handleApplyToJob}
        />
      ) : (
        <ApplicationsTab
          applications={applications}
          jobs={jobs}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          applicationStatusFilter={applicationStatusFilter}
          onApplicationStatusFilterChange={setApplicationStatusFilter}
          dateFromFilter={dateFromFilter}
          onDateFromFilterChange={setDateFromFilter}
          dateToFilter={dateToFilter}
          onDateToFilterChange={setDateToFilter}
          onClearFilters={clearAllFilters}
          onEditApplication={handleEditApplication}
          onViewApplication={setViewingApplication}
          onRateApplication={(app) => { setRatingApplication(app); setShowRatingForm(true); }}
        />
      )}

      {showJobForm && (
        <JobForm
          job={editingJob}
          onSave={handleSaveJob}
          onCancel={() => { setShowJobForm(false); setEditingJob(null); }}
        />
      )}

      {showRatingForm && ratingApplication && (
        <CandidateRatingForm
          existingRatings={ratingApplication.rating}
          assessmentPoints={assessmentPoints}
          evaluatorName="Current User"
          onSave={handleRatingSave}
          onCancel={() => { setShowRatingForm(false); setRatingApplication(null); }}
        />
      )}

      {showCandidateForm && (
        <CandidateFormModal
          editingApplication={editingApplication}
          selectedJobForApply={selectedJobForApply}
          jobs={jobs}
          onSubmit={handleCandidateSubmit}
          onCancel={() => { setShowCandidateForm(false); setEditingApplication(null); setSelectedJobForApply(null); }}
        />
      )}

      {viewingJob && (
        <JobDetailsModal
          job={viewingJob}
          onClose={() => setViewingJob(null)}
          onEdit={handleEditJob}
        />
      )}

      {viewingApplication && (
        <ApplicationDetailsModal
          application={viewingApplication}
          jobs={jobs}
          onClose={() => setViewingApplication(null)}
          onEdit={handleEditApplication}
        />
      )}
    </div>
  );
};
